// =============================================================================
// src/views/workspace/workspace-card-render.ts — HTML dos cards de análise.
//
// Funções PURAS que devolvem strings HTML para o grid de cards e o empty-state.
//   Reusa a classe de paridade `u-ws-card` + estilos inline do mockup original
//   (unified.html), acrescentando data-id e affordances data-act="fav"/"del".
//   Título/meta são escapados (análises criadas trazem texto do usuário).
//
// SRP: só montagem de markup de card. Sem DOM vivo, sem store, sem listeners.
// =============================================================================

import type { WorkspaceAnalysis, WorkspaceTag } from './workspace-types';

/** Cor da estrela quando favoritada (ouro do mockup) vs. neutra. */
const FAVORITE_STAR_COLOR = '#BA7517';

/** Estilo {bg,fg} das pills por tag — paridade com as cores do mockup. */
const WORKSPACE_TAG_STYLES: Record<WorkspaceTag, { readonly bg: string; readonly fg: string }> = {
  eleição: { bg: '#E6F1FB', fg: '#0C447C' },
  'zona norte': { bg: '#EAF3DE', fg: '#3B6D11' },
  '2024': { bg: '#FAEEDA', fg: '#854F0B' },
  urgente: { bg: '#FCEBEB', fg: '#A32D2D' },
  rascunho: { bg: 'var(--color-background-secondary)', fg: 'var(--color-text-secondary)' },
};

/** Escapa texto para interpolação segura em innerHTML. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Junta os cards visíveis num grid; marca o selecionado. */
export function renderCardGrid(
  analyses: readonly WorkspaceAnalysis[],
  selectedId: string | null,
): string {
  return analyses.map((analysis) => renderCard(analysis, analysis.id === selectedId)).join('');
}

/** Markup de um card de análise (data-id + estrela/lixeira acionáveis). */
export function renderCard(analysis: WorkspaceAnalysis, selected: boolean): string {
  const cls = selected ? 'u-ws-card selected' : 'u-ws-card';
  return (
    `<div class="${cls}" data-id="${escapeHtml(analysis.id)}" style="position:relative;border:0.5px solid var(--color-border-tertiary);border-radius:var(--border-radius-lg);padding:12px;cursor:pointer;transition:border-color 0.15s;background:var(--color-background-primary)">` +
    renderCardHeader(analysis) +
    `<div style="font-size:10px;color:var(--color-text-secondary);margin-bottom:8px">${escapeHtml(analysis.meta)}</div>` +
    `<div style="display:flex;gap:4px;flex-wrap:wrap">${renderTagPills(analysis.tags)}</div>` +
    '<i class="ti ti-trash u-ws-card-del" data-act="del" title="Excluir análise" style="position:absolute;right:10px;bottom:10px;font-size:13px;color:var(--color-text-tertiary);cursor:pointer"></i>' +
    '</div>'
  );
}

/** Cabeçalho do card: título + estrela de favorito. */
function renderCardHeader(analysis: WorkspaceAnalysis): string {
  const starColor = analysis.favorite ? FAVORITE_STAR_COLOR : 'var(--color-text-tertiary)';
  return (
    '<div style="display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:8px;gap:8px">' +
    `<div style="font-size:12px;font-weight:500;color:var(--color-text-primary);line-height:1.4">${escapeHtml(analysis.title)}</div>` +
    `<i class="ti ti-star" data-act="fav" title="Favoritar" style="font-size:14px;color:${starColor};flex-shrink:0;cursor:pointer"></i>` +
    '</div>'
  );
}

/** Pills de tag com as cores do mockup. */
function renderTagPills(tags: readonly WorkspaceTag[]): string {
  return tags
    .map((tag) => {
      const style = WORKSPACE_TAG_STYLES[tag];
      return `<span style="font-size:10px;padding:2px 7px;border-radius:999px;background:${style.bg};color:${style.fg}">${escapeHtml(tag)}</span>`;
    })
    .join('');
}

/** Estado vazio (nenhum resultado) com CTA para criar uma nova análise. */
export function renderEmptyState(): string {
  return (
    '<div class="u-ws-empty" style="grid-column:1/-1;display:flex;flex-direction:column;align-items:center;gap:10px;padding:32px 12px;text-align:center;color:var(--color-text-secondary)">' +
    '<i class="ti ti-folder-off" style="font-size:28px;color:var(--color-text-tertiary)"></i>' +
    '<div style="font-size:12px">Nenhuma análise encontrada com estes filtros.</div>' +
    '<button type="button" class="u-cta-btn" data-act="new"><i class="ti ti-plus"></i> Nova análise</button>' +
    '</div>'
  );
}
